import { useEffect, useRef, useState } from "react";
import { notification } from "antd";
import { useDispatch } from "react-redux";

import { getJobShiftSwms } from "../../../../../actions/v2/JobShiftSwmsActions";
import { getJobShiftEmergencyReadiness } from "../../../../../actions/v2/JobShiftEmergencyReadinessActions";
import { Strings } from "../../../../../dataProvider/localize";

export default function useJobShiftReadinessViewModel({ open, context }) {
  const dispatch = useDispatch();
  const controller = useRef();

  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [sections, setSections] = useState({});

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setLoaded(false);
      setReadiness(null);
      setSections({});
      return undefined;
    }

    if (!context || !context.jobId || !context.swmsId || !context.shiftId) {
      setLoading(false);
      setLoaded(true);
      setReadiness(null);
      setSections({});
      return undefined;
    }

    controller.current = new AbortController();
    setLoading(true);
    setLoaded(false);
    setReadiness(null);
    setSections({});

    Promise.all([
      dispatch(getJobShiftSwms(
        {
          jobId: context.jobId,
          shiftId: context.shiftId,
          swmsId: context.swmsId
        },
        { signal: controller.current.signal }
      )),
      dispatch(getJobShiftEmergencyReadiness(
        context.jobId,
        context.shiftId,
        context.swmsId,
        { signal: controller.current.signal }
      ))
    ])
      .then(([swmsResponse, readinessResponse]) => {
        const swmsDocument = swmsResponse && swmsResponse.document
          ? swmsResponse.document
          : swmsResponse;
        setSections(swmsDocument && swmsDocument.sections ? swmsDocument.sections : {});
        setReadiness(readinessResponse || null);
      })
      .catch((error) => {
        if (error && error.name === "AbortError") {
          return;
        }

        const message = typeof error === "string"
          ? error
          : (error && error.message ? error.message : "There was an error while fetching Emergency Readiness.");

        notification.error({
          message: Strings.error_title,
          description: message,
          className: "ant-error"
        });
      })
      .finally(() => {
        setLoading(false);
        setLoaded(true);
      });

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, [dispatch, open, context && context.jobId, context && context.swmsId, context && context.shiftId]);

  return {
    loading,
    loaded,
    readiness,
    sections,
    onReadinessChange: setReadiness
  };
}
